<script setup lang="ts">
import { modifyDate } from '@/shared/utils/modifyTime';
import { ROOT_JOURNAL_URL } from '@/shared/constants/urlConstants';
import type { IJournalArticle } from '@/entities/journal/model/types';

const props = defineProps<{ card: IJournalArticle }>();

const isMobile: boolean = useIsMobile();

const cardLink: ComputedRef<string> = computed(
  () => `${ROOT_JOURNAL_URL}${props.card.topic?.slug}/${props.card.slug}/`,
);
</script>

<template>
  <div :class="['editor-article-card', { _mobile: isMobile }]">
    <NuxtImg
      class="editor-article-card__image"
      :src="card.content?.preview"
      :alt="card.content?.title ?? card.topic?.name"
      sizes="xs:100vw md:185px"
      width="185"
      height="145"
      loading="lazy"
    />
    <div class="editor-article-card__content">
      <div class="editor-article-card__content-header">
        <div class="editor-article-card__category">
          {{ card.topic?.name }}
        </div>
        <NuxtLink class="editor-article-card__title" :to="cardLink">
          {{ card.content?.title }}
        </NuxtLink>
      </div>
      <div class="editor-article-card__description">
        {{ card.content?.description }}
      </div>
      <div class="editor-article-card__info">
        <span class="editor-article-card__info-item">
          <AppIcon class="editor-article-card__icon" name="EyeSmall" />
          {{ card.view_count }}
        </span>
        <span class="editor-article-card__info-item">
          <AppIcon class="editor-article-card__icon" name="CalendarSmall" />
          <time>{{ modifyDate(card.published_at) }}</time>
        </span>
      </div>
    </div>
  </div>
</template>

<style lang="less">
.editor-article-card {
  .box-shadow();

  display: flex;
  gap: @size-x10;
  align-items: flex-start;
  border-radius: @main-size;

  &__image {
    display: block;
    width: 185px;
    height: 145px;
    border-radius: @main-size;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__content {
    .flex-column(@size-x3);

    padding: @size-x5 @size-x5 @size-x5 0;
  }

  &__content-header {
    .flex-column(@main-size);
  }

  &__category {
    font-size: 14px;
    line-height: 130%;
    font-weight: 500;
    color: @color-silver-chalice;
  }

  &__title {
    .color-change-hover();
    .overflow-ellipsis-lines();

    font-size: @size-x4;
    line-height: 130%;
    font-weight: 700;
  }

  &__description {
    .overflow-ellipsis-lines(1);

    font-size: @size-x4;
    line-height: 130%;
    font-weight: 500;
  }

  &__info {
    display: inline-flex;
    align-items: center;
    gap: @size-x6;
  }

  &__info-item {
    display: inline-flex;
    align-items: center;
    gap: @main-size;
    font-size: 14px;
    line-height: 130%;
    font-weight: 500;
  }

  &__icon {
    width: 14px;
    height: 14px;
  }

  &._mobile {
    flex-direction: column;
    gap: @size-x5;
  }

  &._mobile &__image {
    width: 100%;
    height: 160px;
  }

  &._mobile &__content {
    padding: 0 @size-x4 @size-x4 @size-x4;
  }
}
</style>
